// Start, career, briefing, debriefing, leaderboard and settings screens.
import { useEffect, useMemo, useRef, useState } from 'react';
import { api, type User } from './api';
import type { Airport } from '../sim/airport';
import { GameSession } from '../game/session';
import { RadarScope } from './scope/radarScope';
import { LEVELS, CAREER_AIRPORTS, ENDORSEMENTS, type LevelDef } from '../career/levels';
import { isUnlocked, type Career, type ResultApplied } from '../career/progress';
import type { ScoreSummary, SimEvent } from '../sim/scoring';
import { metar, windComponents } from '../sim/weather';
import { Radio, type AudioSettings } from '../audio/radio';
import { speechSupported } from '../audio/speech';
import { typeOf } from '../sim/perf';

const ROLE_LABEL: Record<string, string> = { DEL: 'Delivery', APN: 'Apron', GND: 'Ground', TWR: 'Tower', DEP: 'Departure', APP: 'Approach', DIR: 'Director', CTR: 'Center' };
const STAGE_LABEL: Record<LevelDef['stage'], string> = { ACADEMY: 'Akademie', DEL: 'Freigabe', GND: 'Rollkontrolle', TWR: 'Platzkontrolle', RADAR: 'Radar', CHECKOUT: 'Prüfungen' };
export const fmtTime = (sec: number) => `${Math.floor(sec / 60)}:${String(Math.floor(sec % 60)).padStart(2, '0')}`;

export function BrandMark() {
  return (
    <svg className="brand-mark" viewBox="0 0 48 48" aria-hidden="true">
      <rect width="48" height="48" rx="10" fill="#151b1e" stroke="#2c373e" />
      <path d="M9 31 Q24 7 39 31" fill="none" stroke="#7fb2c9" strokeWidth="2.4" />
      <path d="M15 31 Q24 16 33 31" fill="none" stroke="#7fb2c9" strokeOpacity=".45" strokeWidth="1.6" />
      <circle cx="24" cy="32" r="3.6" fill="#e8c37a" />
    </svg>
  );
}

// ---------------------------------------------------------------------------------------- start
function DemoRadar({ airport }: { airport: Airport }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const demo: LevelDef = { ...LEVELS[0]!, id: 'DEMO', roles: [], minutes: 600, traffic: { minutes: 120, departures: 18, arrivals: 18 }, weather: 'good', assists: { hints: false, conflictPreview: false, suggestions: false, timeControl: true } };
    const s = new GameSession(demo, airport, Math.floor(Math.random() * 1e6));
    s.timeScale = 4;
    s.advance(14 * 60); // start with traffic in the air
    s.start();
    const scope = new RadarScope(ref.current!, s, 30);
    scope.showInfo = false;
    scope.home.cx = -8 * 1852;
    scope.applyHome();
    let raf = 0;
    const frame = () => { scope.draw(); raf = requestAnimationFrame(frame); };
    raf = requestAnimationFrame(frame);
    return () => { cancelAnimationFrame(raf); scope.dispose(); s.dispose(); };
  }, [airport]);
  return <canvas ref={ref} className="start-bg" />;
}

export function StartScreen({ airport, apiOnline, onLogin, onGuest }: { airport: Airport | null; apiOnline: boolean; onLogin: (u: User) => void; onGuest: () => void }) {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [u, setU] = useState(''), [p, setP] = useState(''), [p2, setP2] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    if (mode === 'register' && p !== p2) { setErr('Die Passwörter stimmen nicht überein.'); return; }
    setBusy(true);
    try { const r = mode === 'login' ? await api.login(u.trim(), p) : await api.register(u.trim(), p); onLogin(r.user); }
    catch (ex) { setErr((ex as Error).message); }
    finally { setBusy(false); }
  };
  return (
    <div className="start">
      {airport && <DemoRadar airport={airport} />}
      <div className="start-hero">
        <div className="brand"><BrandMark /><div><div className="brand-name">FREQUENZ</div><div className="brand-sub">Fluglotsen-Simulator</div></div></div>
        <h1>Vom ersten Funkspruch<br /><span>bis Frankfurt Top-Down.</span></h1>
        <p>Lerne Flugsicherung so, wie sie wirklich funktioniert: echte Flughafengeometrie, echte Frequenzen und Verfahren, ICAO-Phraseologie, Pilotenstimmen im Funk, Radar wie am Lotsenarbeitsplatz. Jede Position wird erspielt – Freigabe, Rollkontrolle, Tower, Departure und Approach.</p>
        <div className="airport-strip">
          {CAREER_AIRPORTS.map(a => <span key={a.icao} className={`airport-chip ${a.available ? '' : 'off'}`}><b>{a.icao}</b>{a.name}{a.available ? '' : ' · bald'}</span>)}
        </div>
        <p className="foot-note">Im Hintergrund: Bremen Radar, live simuliert. Flughafengeometrie © OpenStreetMap-Mitwirkende (ODbL). Daten nach AIP Germany, teils angenähert – nur zur Unterhaltung, nicht für die reale Navigation.</p>
      </div>
      <div className="start-card">
        {apiOnline ? (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="tabs">
              <button type="button" className={mode === 'login' ? 'on' : ''} onClick={() => setMode('login')}>Anmelden</button>
              <button type="button" className={mode === 'register' ? 'on' : ''} onClick={() => setMode('register')}>Registrieren</button>
            </div>
            <label className="field">Benutzername<input value={u} onChange={e => setU(e.target.value)} autoComplete="username" maxLength={20} required /></label>
            <label className="field">Passwort<input type="password" value={p} onChange={e => setP(e.target.value)} autoComplete={mode === 'login' ? 'current-password' : 'new-password'} minLength={mode === 'register' ? 8 : undefined} required /></label>
            {mode === 'register' && <label className="field">Passwort wiederholen<input type="password" value={p2} onChange={e => setP2(e.target.value)} autoComplete="new-password" required /></label>}
            {mode === 'register' && <div className="foot-note">3–20 Zeichen (Buchstaben, Zahlen, _ und -), Passwort mindestens 8 Zeichen. Dein Fortschritt und die Bestenliste werden auf dem Server gespeichert.</div>}
            <div className="form-err">{err}</div>
            <button className="btn primary" disabled={busy} type="submit">{mode === 'login' ? 'Anmelden' : 'Konto erstellen'}</button>
          </form>
        ) : <div className="banner info">Der Spielserver ist nicht erreichbar – du kannst als Gast spielen, dein Fortschritt wird in diesem Browser gespeichert.</div>}
        <div className="divider">oder</div>
        <button className="btn" onClick={onGuest}>Als Gast spielen</button>
        <div className="foot-note">Kopfhörer empfohlen. Funkverkehr auf Englisch, Erklärungen auf Deutsch.</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------- career
function Stars({ n }: { n: number }) {
  return <span className="stars">{[0, 1, 2].map(i => <span key={i} className={i < n ? '' : 'off'}>★</span>)}</span>;
}

export function CareerScreen({ user, career, onPlay, onSettings, onBoard, onLogout, onLogin }: { user: User | null; career: Career; onPlay: (l: LevelDef) => void; onSettings: () => void; onBoard: () => void; onLogout: () => void; onLogin: () => void }) {
  const [ap, setAp] = useState('EDDW');
  const levels = LEVELS.filter(l => l.airport === ap);
  const stages = [...new Set(levels.map(l => l.stage))];
  const passed = Object.values(career.levels).filter(l => l.passed).length;
  const starsTotal = Object.values(career.levels).reduce((s, l) => s + l.stars, 0);
  return (
    <div>
      <div className="topnav">
        <div className="brand"><BrandMark /><div className="brand-name" style={{ fontSize: 16 }}>FREQUENZ</div></div>
        <span className="spacer" />
        <span className="chip">{user ? <>Angemeldet als <b>{user.username}</b></> : <>Gastmodus · lokal gespeichert</>}</span>
        {user && <button className="btn small" onClick={onBoard}>Bestenliste</button>}
        <button className="btn small" onClick={onSettings}>Einstellungen</button>
        {user ? <button className="btn small ghost" onClick={onLogout}>Abmelden</button> : <button className="btn small ghost" onClick={onLogin}>Anmelden</button>}
      </div>
      <div className="page">
        <div className="career-head">
          <div>
            <h2>Deine Laufbahn</h2>
            <p className="muted" style={{ margin: 0, maxWidth: 640 }}>Wie in der echten Ausbildung: Akademie, Training an jeder Position, Checkout. Realismus bleibt immer gleich – schwieriger werden Verkehr, Wetter, Störungen und die Zahl der Hilfen.</p>
            <div className="stats-row">
              <div className="stat"><b>{career.xp.toLocaleString('de-DE')}</b><span>Erfahrung (XP)</span></div>
              <div className="stat"><b>{passed}/{LEVELS.length}</b><span>Level bestanden</span></div>
              <div className="stat"><b>{starsTotal}</b><span>Sterne</span></div>
              <div className="stat"><b>{career.endorsements.length}</b><span>Berechtigungen</span></div>
            </div>
          </div>
          <div className="licence">
            <h4>Fluglotsenlizenz · Training</h4>
            <div className="holder">{user?.username ?? 'Gast'}</div>
            <ul>
              {career.endorsements.length ? career.endorsements.map(e => <li key={e}><span className="code">{ENDORSEMENTS[e]?.title ?? e}</span>{ENDORSEMENTS[e]?.text}</li>)
                : <li className="empty">Noch keine Berechtigung – bestehe deinen ersten Checkout.</li>}
            </ul>
          </div>
        </div>
        <div className="airports">
          {CAREER_AIRPORTS.map(a => {
            const open = a.available;
            return (
              <div key={a.icao} className={`ap-card ${ap === a.icao ? 'on' : ''} ${open ? '' : 'locked'}`} onClick={() => open && setAp(a.icao)}>
                <div className="icao">{a.icao}</div>
                <h3>{a.name}</h3>
                <p>{a.blurb}</p>
                {!open && <div className="lock">In Vorbereitung · Freischaltung nach {a.icao === 'EDDH' ? 'Top-Down Bremen' : a.icao === 'EDDM' ? 'Hamburg' : 'München'}</div>}
              </div>
            );
          })}
        </div>
        {stages.map(st => (
          <div key={st}>
            <div className="stage"><h3>{STAGE_LABEL[st]}</h3><span className="line" /></div>
            <div className="levels">
              {levels.filter(l => l.stage === st).map(l => {
                const pr = career.levels[l.id];
                const open = isUnlocked(career, l);
                return (
                  <div key={l.id} className={`level ${open ? '' : 'locked'} ${l.stage === 'CHECKOUT' ? 'checkout' : ''}`} onClick={() => open && onPlay(l)} title={open ? '' : `Freischaltung: ${l.requires.map(r => LEVELS.find(x => x.id === r)?.title).join(', ')}`}>
                    <div className="lid"><span>{l.id}</span><Stars n={pr?.stars ?? 0} /></div>
                    <h4>{l.title}</h4>
                    <div className="sub">{l.subtitle}</div>
                    <div className="meta">
                      <span className="roles">{l.roles.map(r => <span key={r} className="role">{ROLE_LABEL[r]}</span>)}</span>
                      <span>{l.minutes} min</span>
                      {pr && <span>Bestwert {pr.best}</span>}
                      {!open && <span>🔒</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        <p className="foot-note" style={{ marginTop: 30 }}>Flughafengeometrie © OpenStreetMap-Mitwirkende (ODbL). Betriebsdaten nach AIP Germany (AIRAC 10/26) und öffentlichen Quellen, teilweise angenähert. Stimmen: Piper TTS, Sprecher aus LibriTTS-R (CC BY 4.0). Nur zur Unterhaltung, nicht für die reale Luftfahrt.</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------- briefing
export function BriefingScreen({ level, session, onBack, onStart }: { level: LevelDef; session: GameSession; onBack: () => void; onStart: () => void }) {
  const w = session.world;
  const end = session.airport.end(w.config.arr[0]!);
  const wc = windComponents(w.weather, end.hdg);
  const units = w.playerUnits().map(u => w.unit(u));
  return (
    <div className="sheet">
      <div className="kicker">{level.id} · {session.airport.meta.name} {session.airport.icao} · {level.minutes} Minuten</div>
      <h2>{level.title}</h2>
      <div className="muted">{level.subtitle}</div>
      {level.briefing.map((p, i) => <p key={i}>{p}</p>)}
      <div className="grid2">
        <div className="box">
          <h5>Deine Position</h5>
          {units.map(u => <div key={u.id} className="mono">{u.callsign} · {u.freq}{u.real ? '' : ' (Ausbildungsposition)'}</div>)}
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Aufgaben: {level.roles.map(r => ROLE_LABEL[r]).join(', ')}</div>
        </div>
        <div className="box">
          <h5>Übergabe · Wetter</h5>
          <div className="metar">{metar(session.airport.icao, w.weather, w.utc())}</div>
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{w.config.name} · ATIS {w.atis.letter} · TL {w.tl} · Gegenwind {Math.round(wc.head)} kt, Seitenwind {Math.abs(Math.round(wc.cross))} kt{w.lvp ? ' · LVP in Kraft' : ''}</div>
        </div>
        <div className="box">
          <h5>Ziele</h5>
          <ul>{level.goals.map(g => <li key={g}>{g}</li>)}</ul>
        </div>
        <div className="box">
          <h5>Bedingungen</h5>
          <ul>
            <li>Bestehen ab {level.passScore} Punkten, ohne schwerwiegendes Ereignis</li>
            <li>{level.assists.hints ? 'Ausbilderin gibt Hinweise' : 'Keine Hinweise'}{level.assists.timeControl ? ' · Zeitraffer und Pause erlaubt' : ' · kein Zeitraffer'}</li>
            <li>{level.readbackErrorRate > 0 ? 'Piloten lesen gelegentlich falsch zurück' : 'Readbacks fehlerfrei'}</li>
            <li>Verkehr: {level.traffic.departures} Abflüge, {level.traffic.arrivals + (level.traffic.onFinal?.length ?? 0)} Anflüge</li>
          </ul>
        </div>
      </div>
      <div className="actions">
        <button className="btn ghost" onClick={onBack}>Zurück</button>
        <button className="btn primary" onClick={onStart}>Position übernehmen</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------- debrief
const SEV_LABEL: Record<string, string> = { critical: 'Schwerwiegend', major: 'Erheblich', minor: 'Gering', info: 'Hinweis' };
export function DebriefScreen({ level, summary, events, result, saveError, career, onCareer, onRetry, onNext }: { level: LevelDef; summary: ScoreSummary; events: SimEvent[]; result: ResultApplied; saveError: string | null; career: Career; onCareer: () => void; onRetry: () => void; onNext: (id: string) => void }) {
  const [shown, setShown] = useState(0);
  useEffect(() => { const t = setTimeout(() => setShown(1), 80); return () => clearTimeout(t); }, []);
  const next = result.unlocked[0] ?? LEVELS.find(l => isUnlocked(career, l) && !career.levels[l.id]?.passed && l.id !== level.id);
  const mmss = (t: number) => `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(Math.floor(t % 60)).padStart(2, '0')}`;
  return (
    <div className="sheet">
      <div className="kicker">Nachbesprechung · {level.id}</div>
      <h2>{summary.relieved ? 'Von der Position abgelöst' : result.passed ? 'Bestanden' : 'Nicht bestanden'}</h2>
      {summary.relieved && <div className="banner bad">{summary.relievedReason} – nach einem schweren Vorfall übernimmt die Supervisorin deine Position.</div>}
      {!summary.relieved && !result.passed && <div className="banner info">Bestehen ab {level.passScore} Punkten. Sieh dir die Ereignisse unten an und versuch es noch einmal.</div>}
      {result.newEndorsement && <div className="banner ok">Neue Berechtigung auf deiner Lizenz: {ENDORSEMENTS[result.newEndorsement]?.title} – {ENDORSEMENTS[result.newEndorsement]?.text}</div>}
      {result.unlocked.map(l => <div key={l.id} className="banner ok">Freigeschaltet: {l.title}</div>)}
      {saveError && <div className="banner bad">{saveError}</div>}
      <div className="grid2" style={{ marginTop: 14 }}>
        <div>
          <div className="score-big"><b>{summary.total}</b><span className="muted">Punkte · +{result.xpGained} XP</span></div>
          <div style={{ margin: '6px 0 16px' }}><Stars n={result.newStars} /></div>
          <div className="bars">
            {([['Sicherheit', summary.safety], ['Effizienz', summary.efficiency], ['Kommunikation', summary.comms]] as const).map(([k, v]) => (
              <div key={k} className="bar-row"><span>{k}</span><div className="bar"><div style={{ width: `${shown * v}%`, background: v < 60 ? 'var(--bad)' : v < 80 ? 'var(--warn)' : 'var(--ok)' }} /></div><span className="mono">{v}</span></div>
            ))}
          </div>
        </div>
        <div className="box">
          <h5>Schicht</h5>
          <ul>
            <li>{summary.movements} abgeschlossene Bewegungen, {summary.playerMovements} davon unter deiner Kontrolle</li>
            <li>Mittlere Antwortzeit {summary.avgResponse} s</li>
            <li>Falsche Readbacks: {summary.readbackErrors}, davon {summary.readbackCaught} erkannt</li>
            <li>Durchstartmanöver: {summary.goArounds}</li>
          </ul>
        </div>
      </div>
      <h5 style={{ margin: '18px 0 6px', fontSize: 11, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--muted)' }}>Ereignisse</h5>
      <div className="events">
        {events.length ? events.map(e => (
          <div key={e.id} className={`ev ${e.sev}`}><span className="t">{mmss(e.t)}</span><span><b>{SEV_LABEL[e.sev]}{e.points ? ` (${e.points > 0 ? '+' : ''}${e.points})` : ''}:</b> {e.text}</span></div>
        )) : <div className="muted">Keine besonderen Ereignisse – saubere Arbeit.</div>}
      </div>
      <div className="actions">
        <button className="btn ghost" onClick={onCareer}>Zur Laufbahn</button>
        <button className="btn" onClick={onRetry}>Noch einmal</button>
        {next && next.id !== level.id && <button className="btn primary" onClick={() => onNext(next.id)}>Weiter: {next.title}</button>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------- leaderboard
export function LeaderboardScreen({ user, onBack }: { user: User | null; onBack: () => void }) {
  const [rows, setRows] = useState<{ username: string; xp: number }[] | null>(null);
  useEffect(() => { api.leaderboard().then(r => setRows(r.rows)).catch(() => setRows([])); }, []);
  return (
    <div className="sheet">
      <h2>Bestenliste</h2>
      {!rows ? <p className="muted">Lade …</p> : !rows.length ? <p className="muted">Keine Einträge.</p> : (
        <table className="board"><thead><tr><th>#</th><th>Lotse</th><th>XP</th></tr></thead>
          <tbody>{rows.map((r, i) => <tr key={r.username} className={r.username === user?.username ? 'me' : ''}><td>{i + 1}</td><td>{r.username}</td><td className="mono">{r.xp.toLocaleString('de-DE')}</td></tr>)}</tbody>
        </table>
      )}
      <div className="actions"><button className="btn" onClick={onBack}>Zurück</button></div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------- settings
export function SettingsModal({ onClose }: { onClose: () => void }) {
  const [s, setS] = useState<AudioSettings>({ ...Radio.s });
  const set = <K extends keyof AudioSettings>(k: K, v: AudioSettings[K]) => { Radio.set(k, v); setS({ ...Radio.s }); };
  const tog = (k: keyof AudioSettings, label: string, sub: string) => (
    <label className="toggle"><input type="checkbox" checked={!!s[k]} onChange={e => set(k, e.target.checked as never)} /><span>{label}<small>{sub}</small></span></label>
  );
  const test = () => {
    Radio.unlock();
    Radio.play({ id: -1, t: 0, dur: 3, freq: 'TWR', from: 'PILOT', cs: 'DLH4TK', text: '', spoken: 'Bremen Tower, Lufthansa four Tango Kilo, holding point Foxtrot, ready for departure.', voice: 'DLH4TK' }, 1);
  };
  const speech = useMemo(() => speechSupported(), []);
  return (
    <div className="modal-bg" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <h3>Einstellungen</h3>
        <label className="field">Lautstärke<input type="range" min={0} max={1} step={0.05} value={s.master} onChange={e => set('master', Number(e.target.value))} /></label>
        {tog('voices', 'Funkstimmen', 'Piloten und Kollegen werden vorgelesen')}
        {tog('ownVoice', 'Eigene Funksprüche vorlesen', 'Deine Anweisungen in Lotsenstimme')}
        {tog('natural', 'Natürliche Stimmen', 'Neuronale Sprachausgabe (Piper) vom Spielserver – sonst die Stimme deines Browsers')}
        {tog('radioFx', 'Funkgerät-Klang', 'Bandbegrenzung, Übersteuerung und Rauschen wie im Flugfunk')}
        {tog('sfx', 'Klicks und Squelch', 'Sendetaste, Squelch-Ende, Alarmton')}
        {tog('otherFreqs', 'Andere Frequenzen im Protokoll', 'Funkverkehr der KI-Kollegen grau anzeigen')}
        <div className="foot-note" style={{ margin: '10px 0' }}>
          Spracheingabe: Halte die Leertaste (oder den Sendeknopf) gedrückt und sprich die Anweisung auf Englisch. {speech
            ? 'Dein Browser unterstützt Spracherkennung. Hinweis: In Chrome und Edge wird das Audio dafür vom Spracherkennungsdienst des Browserherstellers verarbeitet.'
            : 'Dein Browser unterstützt keine Spracherkennung – nutze Chrome oder Edge oder die Eingabezeile.'}
        </div>
        <div className="actions"><button className="btn" onClick={test}>Stimme testen</button><button className="btn primary" onClick={onClose}>Fertig</button></div>
        <div className="foot-note">Flugzeugtypen im Spiel: {['A320', 'B738', 'E190', 'CRJ9', 'DH8D', 'A388'].map(t => typeOf(t).name).join(', ')} u. a.</div>
      </div>
    </div>
  );
}
